import { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Typography, useTheme, Alert, Button } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useSelector } from "react-redux";
import { isEqual } from "lodash";
import MVPMapWidget from "./widgets/MVPMapWidget/MVPMapWidget";
import InventoryHistoryGrid from "./widgets/InventoryHistoryGrid";
import AssetsTrackersOverview from "./widgets/AssetsTrackersOverview/AssetsTrackersOverview";
import ActivityFeed from "./widgets/ActivityFeed/ActivityFeed";
import FacilityUtilization from "./widgets/FacilityUtilization/FacilityUtilization";
import RecentEvents from "./widgets/RecentEvents/RecentEvents";
import NotificationsFeed from "./widgets/NotificationsFeed/NotificationsFeed";
import ErrorBoundary from "../../components/ErrorBoundary/ErrorBoundary";
import { useNotificationsOptional } from "../../context/NotificationsContext";
import AssetTimeline from "../../components/AssetTimeline/AssetTimeline";
import WidgetFullscreen from "../../components/WidgetFullscreen/WidgetFullscreen";
import { DashboardSkeleton } from "../../components/SkeletonLoader/SkeletonLoader";
import DashboardIcon from "@mui/icons-material/Dashboard";
import RefreshIcon from "@mui/icons-material/Refresh";
import MapIcon from "@mui/icons-material/Map";
import TimelineIcon from "@mui/icons-material/Timeline";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HistoryIcon from "@mui/icons-material/History";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { searchAssets } from "../../utils/API/Assets/Assets";
import { getDevices } from "../../utils/API/Devices/Devices";

export default function Dashboard(props) {
  const { userAvailableViews, apiUrl, token, timeZone } = props;
  
  // Get facilities from Redux
  const { facilities } = useSelector((state) => state.organization, isEqual);
  
  // Local state for fetched data
  const [dashboardData, setDashboardData] = useState({
    assets: [],
    devices: [],
    loading: true,
    error: null,
  });
  const { assets, devices, loading, error } = dashboardData;

  // UI state for new features
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  // Memoize fetch function for retry capability
  const fetchDashboardData = useCallback(async () => {
    setDashboardData(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // Fetch assets and devices in parallel
      const [assetResult, deviceResult] = await Promise.all([
        searchAssets(props, {
          limit: 1000,
          startDate: null,
          endDate: null,
        }),
        getDevices(props),
      ]);
      
      setDashboardData({
        assets: assetResult?.assets || [],
        devices: deviceResult?.assets || [],
        loading: false,
        error: null,
      });
    } catch (err) {
      // Only log in development
      if (process.env.NODE_ENV === "development") {
        console.error("Error fetching dashboard data:", err);
      }
      setDashboardData(prev => ({
        ...prev,
        loading: false,
        error: err?.message || "Failed to load dashboard data",
      }));
    }
  }, [apiUrl, token]);

  // Fetch dashboard data on mount and when dependencies change
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);




  
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, background = {}, text = {}, divider, mode } = palette;
  const isDark = mode === "dark";
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const textPrimary = text?.primary || (isDark ? "#ffffff" : "#1e293b");
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0");

  const assetOrInvPermissions =
    userAvailableViews.includes("View Assets") ||
    userAvailableViews.includes("View Inventory");
  const viewInventory = userAvailableViews.includes("View Inventory");
  const viewAssetHistory = userAvailableViews.includes("View Assets");

  const setNotificationSource = useNotificationsOptional()?.setNotificationSource;
  useEffect(() => {
    if (setNotificationSource && !loading) {
      setNotificationSource({
        assets: assets || [],
        facilities: facilities || {},
      });
    }
  }, [assets, facilities, loading, setNotificationSource]);

  // Handler for facility click - memoized to prevent unnecessary re-renders
  const handleFacilityClick = useCallback((facilityId) => {
    // Could filter the map or navigate to facility detail
    // Future: dispatch(setSelectedFacility(facilityId))
  }, []);

  // Show loading state with skeleton
  if (loading) {
    return (
      <Box
        role="status"
        aria-label="Loading dashboard"
        aria-busy="true"
      >
        <DashboardSkeleton />
      </Box>
    );
  }

  // Show error state with retry option
  if (error) {
    return (
      <Box
        role="alert"
        aria-live="assertive"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 2,
          padding: "32px",
        }}
      >
        <Alert 
          severity="error"
          sx={{ marginBottom: 2, maxWidth: "500px" }}
        >
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchDashboardData}
          aria-label="Retry loading dashboard"
          sx={{
            backgroundColor: greenColor,
            "&:hover": {
              backgroundColor: navyColor,
            },
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    assetOrInvPermissions && (
      <Box
        component="main"
        role="main"
        aria-label="Dashboard"
        sx={{
          animation: "dashboardFadeIn 0.5s ease-out",
          "@keyframes dashboardFadeIn": {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      >
        <Box
          sx={{
            maxWidth: "1600px",
            margin: "0 auto",
            padding: { xs: "4px 16px", sm: "6px 20px", md: "8px 24px" },
            backgroundColor: isDark ? "rgba(0, 0, 0, 0.02)" : "rgba(248, 250, 252, 0.6)",
            borderRadius: { xs: 0, md: "16px" },
            transition: "background-color 0.2s ease",
          }}
        >
        {/* Dashboard Header */}
        <Box
          component="header"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            paddingBottom: "14px",
            borderBottom: `1px solid ${borderColor}`,
            animation: "headerSlide 0.4s ease-out",
            "@keyframes headerSlide": {
              from: { opacity: 0, transform: "translateY(-10px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box
              aria-hidden="true"
              sx={{
                width: 42,
                height: 42,
                borderRadius: "10px",
                background: isDark
                  ? `linear-gradient(135deg, ${greenColor}25 0%, ${greenColor}10 100%)`
                  : `linear-gradient(135deg, ${greenColor}20 0%, ${navyColor}10 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${greenColor}${isDark ? '40' : '30'}`,
                boxShadow: `0 4px 12px ${greenColor}${isDark ? '20' : '15'}`,
              }}
            >
              <DashboardIcon sx={{ fontSize: "1.25rem", color: greenColor }} />
            </Box>
            <Box>
              <Typography
                variant="h1"
                sx={{
                  color: isDark ? greenColor : navyColor,
                  fontWeight: 700,
                  fontSize: "1.5rem",
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                  fontFamily: '"Lato", sans-serif',
                }}
              >
                Dashboard
              </Typography>
              <Typography
                sx={{
                  color: textSecondary,
                  fontSize: "0.8125rem",
                  marginTop: "1px",
                  lineHeight: 1.25,
                }}
              >
                Overview of your assets and inventory
              </Typography>
            </Box>
          </Box>

        </Box>

        {/* Top row: Assets & Trackers Overview (Total Assets + Active Trackers + Tracker Count), Notifications */}
        <Grid
          container
          spacing={3}
          sx={{ marginBottom: "24px" }}
          component="section"
          aria-label="Overview"
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ height: "100%", minHeight: "320px", animation: "cardSlideIn 0.4s ease-out 0.1s both" }}>
              <WidgetFullscreen title="Assets & Trackers" icon={<AnalyticsIcon />}>
                <ErrorBoundary componentName="Assets & Trackers Overview">
                  <AssetsTrackersOverview assets={assets} devices={devices} />
                </ErrorBoundary>
              </WidgetFullscreen>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ height: "100%", minHeight: "320px", animation: "cardSlideIn 0.4s ease-out 0.15s both" }}>
              <WidgetFullscreen title="Notifications" icon={<NotificationsActiveIcon />}>
                <ErrorBoundary componentName="Notifications">
                  <NotificationsFeed timeZone={timeZone} />
                </ErrorBoundary>
              </WidgetFullscreen>
            </Box>
          </Grid>
        </Grid>

        {/* Row 1: Map full width */}
        <Grid
          container
          spacing={3}
          sx={{ marginBottom: "24px" }}
          component="section"
          aria-label="Asset Map"
        >
          <Grid size={12}>
            <Box
              sx={{
                height: "500px",
                borderRadius: "16px",
                overflow: "hidden",
                animation: "cardSlideIn 0.4s ease-out 0.1s both",
                "@keyframes cardSlideIn": {
                  from: { opacity: 0, transform: "translateY(16px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <WidgetFullscreen title="Asset Map" icon={<MapIcon />}>
                <ErrorBoundary componentName="Map">
                  <MVPMapWidget {...props} />
                </ErrorBoundary>
              </WidgetFullscreen>
            </Box>
          </Grid>
        </Grid>

        {/* Row 2: Recent Events, Activity Feed, Facility Utilization */}
        <Grid
          container
          spacing={3}
          sx={{ marginBottom: "24px" }}
          component="section"
          aria-label="Overview and Activity"
        >
          {/* Recent Events */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box
              sx={{
                height: "400px",
                animation: "cardSlideIn 0.4s ease-out 0.3s both",
              }}
            >
              <WidgetFullscreen title="Recent Events" icon={<HistoryIcon />}>
                <ErrorBoundary componentName="Recent Events">
                  <RecentEvents
                    assets={assets}
                    facilities={facilities}
                    timeZone={timeZone}
                  />
                </ErrorBoundary>
              </WidgetFullscreen>
            </Box>
          </Grid>

          {/* Activity Feed */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box
              sx={{
                height: "400px",
                animation: "cardSlideIn 0.4s ease-out 0.4s both",
              }}
            >
              <WidgetFullscreen title="Activity Feed" icon={<TimelineIcon />}>
                <ErrorBoundary componentName="Activity Feed">
                  <ActivityFeed
                    assets={assets}
                    facilities={facilities}
                    timeZone={timeZone}
                  />
                </ErrorBoundary>
              </WidgetFullscreen>
            </Box>
          </Grid>

          {/* Facility Utilization */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box
              sx={{
                height: "400px",
                animation: "cardSlideIn 0.4s ease-out 0.5s both",
              }}
            >
              <WidgetFullscreen title="Facility Utilization" icon={<TrendingUpIcon />}>
                <ErrorBoundary componentName="Facility Utilization">
                  <FacilityUtilization
                    assets={assets}
                    facilities={facilities}
                    onFacilityClick={handleFacilityClick}
                  />
                </ErrorBoundary>
              </WidgetFullscreen>
            </Box>
          </Grid>
        </Grid>

        {/* Inventory/Asset History Grid - Full Width */}
        <Grid 
          container 
          spacing={3}
          component="section"
          aria-label="Asset and Inventory History"
        >
          <Grid size={12}>
            <Box
              sx={{
                minHeight: "400px",
                borderRadius: "16px",
                overflow: "hidden",
                animation: "cardSlideIn 0.4s ease-out 0.6s both",
                border: `1px solid ${borderColor}`,
                boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 4px 20px rgba(0, 0, 0, 0.06)",
                background: isDark
                  ? "linear-gradient(145deg, #032d42 0%, #021e2d 100%)"
                  : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
                position: "relative",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: isDark
                    ? `linear-gradient(90deg, ${greenColor} 0%, ${navyColor} 100%)`
                    : `linear-gradient(90deg, ${navyColor} 0%, ${greenColor} 100%)`,
                  borderRadius: "16px 16px 0 0",
                },
              }}
            >
              <ErrorBoundary componentName="History Grid">
                <InventoryHistoryGrid
                  {...props}
                  viewAssetHistory={viewAssetHistory}
                  viewInventory={viewInventory}
                />
              </ErrorBoundary>
            </Box>
          </Grid>
        </Grid>

        {/* Asset Timeline Dialog */}
        <AssetTimeline
          open={timelineOpen}
          onClose={() => {
            setTimelineOpen(false);
            setSelectedAsset(null);
          }}
          asset={selectedAsset}
          events={[]} // Could pass asset history events here
          facilities={facilities || {}}
          onEventClick={(event) => {
            // Could zoom map to event location
          }}
          onViewOnMap={(event) => {
            // Could center map on this location
          }}
        />

        </Box>
      </Box>
    )
  );
}
